import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getOTDetails,
  approveOT,
  bulkApproveOT,
  getOTDetailsWithStatus,
  getOTRateHistory,
} from '../../services/OTDetailsService';


const normaliseAttDay = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const m = raw.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}`;
  return raw;
};

// fetchOTDetails
export const fetchOTDetails = createAsyncThunk(
  'overtime/fetchOTDetails',
  async ({ employeeId, attDay, isApproved } = {}, { rejectWithValue }) => {
    try {
      const params = {};

      if (employeeId && String(employeeId).trim()) {
        params.employeeId = String(employeeId).trim();
      }

      const month = normaliseAttDay(attDay);
      if (month) {
        params.attDay = month; // YYYY-MM
      }

      if (isApproved !== undefined && isApproved !== null) {
        params.isApproved = isApproved;
      }

      const rows = await getOTDetails(params);

      return {
        employeeId: params.employeeId || '',
        attDay: params.attDay || '',          // normalised month
        isApproved: isApproved !== undefined ? isApproved : null,
        rows,
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

// fetchOTDetailsWithStatus
export const fetchOTDetailsWithStatus = createAsyncThunk(
  'overtime/fetchOTDetailsWithStatus',
  async ({ employeeId, attDay, fromDate, toDate } = {}, { rejectWithValue }) => {
    try {
      // ─────────────────────────────────────────────────────
      // DATE-RANGE MODE
      // ─────────────────────────────────────────────────────
      if (fromDate && toDate) {
        const months = monthsInRange(fromDate, toDate);

        if (months.length === 0) {
          return {
            employeeId: employeeId || '',
            attDay: '',
            fromDate, toDate,
            all: [], pending: [], approved: [],
          };
        }

        const results = await Promise.all(
          months.map((m) =>
            getOTDetailsWithStatus({ employeeId, attDay: m })
          )
        );

        const mergedAll = dedupeById(results.flatMap((r) => r.all || []));
        const mergedPending = dedupeById(results.flatMap((r) => r.pending || []));
        const mergedApproved = dedupeById(results.flatMap((r) => r.approved || []));

        return {
          employeeId: employeeId || '',
          attDay: '',
          fromDate,
          toDate,
          all: filterByRange(mergedAll, fromDate, toDate),
          pending: filterByRange(mergedPending, fromDate, toDate),
          approved: filterByRange(mergedApproved, fromDate, toDate),
        };
      }

      const month = normaliseAttDay(attDay);
      const result = await getOTDetailsWithStatus({ employeeId, attDay: month });

      return {
        employeeId: employeeId || '',
        attDay: month,
        fromDate: '',
        toDate: '',
        ...result,
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

// approveOTRecord - Single approval
export const approveOTRecord = createAsyncThunk(
  'overtime/approveOTRecord',
  async ({ employeeId, overtimeId, approvedBy }, { rejectWithValue }) => {
    try {
      const result = await approveOT({ employeeId, overtimeId, approvedBy });
      return { overtimeId, result };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to approve OT record.');
    }
  }
);

// bulkApproveOTRecords - Bulk approval
export const bulkApproveOTRecords = createAsyncThunk(
  'overtime/bulkApproveOTRecords',
  async ({ records, approvedBy }, { rejectWithValue }) => {
    try {
      const result = await bulkApproveOT({ records, approvedBy });
      return { records, result };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to approve OT records.');
    }
  }
);

/* ── Range helpers ─────────────────────────────────────────── */

// 'YYYY-MM' list between two 'YYYY-MM-DD' dates
const monthsInRange = (fromDate, toDate) => {
  const out = [];
  const start = new Date(`${fromDate}T00:00:00`);
  const end = new Date(`${toDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return out;
  if (start > end) return out;

  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);

  while (cur <= last) {
    out.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
};

// Dedupe by OT id, keeping the last occurrence
const dedupeById = (rows = []) => {
  const map = new Map();
  rows.forEach((r, i) => {
    const id = r?.HOT_OVERTIME_ID ?? `__orphan_${i}`;
    map.set(String(id), r);
  });
  return Array.from(map.values());
};


// Parse any of the backend date formats into 'YYYY-MM-DD'
const rowDate = (row) => {
  const raw =
    row?.HOT_ATTENDANCE_DAY ??
    row?.HOT_OT_DATE ??
    row?.HOT_DATE ??
    row?.date ??
    '';
  const s = String(raw).trim();
  if (!s) return '';

  // ISO: YYYY-MM-DD or YYYY-MM-DDTHH
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // Month only: YYYY-MM
  const monthOnly = s.match(/^(\d{4})-(\d{2})$/);
  if (monthOnly) return `${monthOnly[1]}-${monthOnly[2]}`;

  // US format: M/D/YYYY or MM/DD/YYYY
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    const [, m, d, y] = us;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
};

// Inclusive range filter
const filterByRange = (rows, fromDate, toDate) =>
  (rows || []).filter((r) => {
    const d = rowDate(r);
    if (!d) return true;                  
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      return d >= fromDate && d <= toDate;     
    }
    return true;                             
  });

export const fetchOTRateHistory = createAsyncThunk(
  'overtime/fetchOTRateHistory',
  async (sno, { rejectWithValue }) => {
    try {
      const rows = await getOTRateHistory(sno);
      return rows;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch OT history');
    }
  }
);

const initialState = {
  employeeId: '',
  attDay: '',
  fromDate: '',
  toDate: '',
  isApproved: null,
  rows: [],
  pendingRows: [],
  approvedRows: [],
  loading: false,
  approving: false,
  error: null,
  approvalError: null,
  bulkApprovalResult: null,

  otRateHistory: [],
  otRateHistoryLoading: false,
  otRateHistoryError: null,
};

const overtimeSlice = createSlice({
  name: 'overtime',
  initialState,
  reducers: {
    clearOvertimeError(state) {
      state.error = null;
      state.approvalError = null;
      state.bulkApprovalResult = null;
    },
    resetOvertime(state) {
      state.employeeId = '';
      state.attDay = '';
      state.isApproved = null;
      state.rows = [];
      state.pendingRows = [];
      state.approvedRows = [];
      state.attDay = '';
      state.fromDate = '';
      state.toDate = '';
      state.error = null;
      state.approvalError = null;
      state.bulkApprovalResult = null;

      state.otRateHistory = [];
      state.otRateHistoryLoading = false;
      state.otRateHistoryError = null;
    },
    setApprovalFilter(state, action) {
      state.isApproved = action.payload;
    },
    clearBulkApprovalResult(state) {
      state.bulkApprovalResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchOTDetails
      .addCase(fetchOTDetails.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.employeeId = action.meta.arg?.employeeId || '';
        state.attDay = normaliseAttDay(action.meta.arg?.attDay);
        state.isApproved =
          action.meta.arg?.isApproved !== undefined ? action.meta.arg.isApproved : null;
      })
      .addCase(fetchOTDetails.fulfilled, (state, action) => {
        const { employeeId, attDay, isApproved, rows } = action.payload;
        state.loading = false;
        state.employeeId = employeeId;
        state.attDay = attDay;
        state.isApproved = isApproved;
        state.rows = rows || [];

        state.pendingRows = rows.filter(
          (r) => r.HOT_IS_APPROVED !== 'True' && r.HOT_IS_APPROVED !== true
        );
        state.approvedRows = rows.filter(
          (r) => r.HOT_IS_APPROVED === 'True' || r.HOT_IS_APPROVED === true
        );
      })
      .addCase(fetchOTDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load overtime details';
        state.rows = [];
        state.pendingRows = [];
        state.approvedRows = [];
      })

      // fetchOTDetailsWithStatus
      .addCase(fetchOTDetailsWithStatus.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.employeeId = action.meta.arg?.employeeId || '';
        state.attDay = normaliseAttDay(action.meta.arg?.attDay);
        state.fromDate = action.meta.arg?.fromDate || '';
        state.toDate = action.meta.arg?.toDate || '';
      })
      .addCase(fetchOTDetailsWithStatus.fulfilled, (state, action) => {
        const { employeeId, attDay, fromDate, toDate, all, pending, approved } = action.payload;
        state.loading = false;
        state.employeeId = employeeId;
        state.attDay = attDay;
        state.fromDate = fromDate || '';
        state.toDate = toDate || '';
        state.rows = all || [];
        state.pendingRows = pending || [];
        state.approvedRows = approved || [];
        state.isApproved = null;
      })
      .addCase(fetchOTDetailsWithStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load overtime details';
        state.rows = [];
        state.pendingRows = [];
        state.approvedRows = [];
      })

      // approveOTRecord - Single
      .addCase(approveOTRecord.pending, (state) => {
        state.approving = true;
        state.approvalError = null;
      })
      .addCase(approveOTRecord.fulfilled, (state, action) => {
        state.approving = false;
        const { overtimeId } = action.payload;

        const updateRow = (row) =>
          row.HOT_OVERTIME_ID === overtimeId
            ? { ...row, HOT_IS_APPROVED: 'True' }
            : row;

        state.rows = state.rows.map(updateRow);
        state.approvedRows = state.approvedRows.map(updateRow);

        const rowToMove = state.pendingRows.find(
          (r) => r.HOT_OVERTIME_ID === overtimeId
        );
        if (rowToMove) {
          state.pendingRows = state.pendingRows.filter(
            (r) => r.HOT_OVERTIME_ID !== overtimeId
          );
          state.approvedRows = [
            ...state.approvedRows,
            { ...rowToMove, HOT_IS_APPROVED: 'True' },
          ];
        }
      })
      .addCase(approveOTRecord.rejected, (state, action) => {
        state.approving = false;
        state.approvalError = action.payload ?? 'Failed to approve OT record';
      })

      // bulkApproveOTRecords
      .addCase(bulkApproveOTRecords.pending, (state) => {
        state.approving = true;
        state.approvalError = null;
        state.bulkApprovalResult = null;
      })
      .addCase(bulkApproveOTRecords.fulfilled, (state, action) => {
        state.approving = false;
        const { records, result } = action.payload;

        state.bulkApprovalResult = result;

        if (result.succeeded > 0) {
          const approvedIds = records
            .filter((r) =>
              result.results?.some((x) => x.overtimeId === r.overtimeId)
            )
            .map((r) => r.overtimeId);

          const updateRow = (row) =>
            approvedIds.includes(row.HOT_OVERTIME_ID)
              ? { ...row, HOT_IS_APPROVED: 'True' }
              : row;

          state.rows = state.rows.map(updateRow);
          state.approvedRows = state.approvedRows.map(updateRow);

          const rowsToMove = state.pendingRows.filter((r) =>
            approvedIds.includes(r.HOT_OVERTIME_ID)
          );
          if (rowsToMove.length > 0) {
            state.pendingRows = state.pendingRows.filter(
              (r) => !approvedIds.includes(r.HOT_OVERTIME_ID)
            );
            state.approvedRows = [
              ...state.approvedRows,
              ...rowsToMove.map((r) => ({ ...r, HOT_IS_APPROVED: 'True' })),
            ];
          }
        }
      })
      .addCase(bulkApproveOTRecords.rejected, (state, action) => {
        state.approving = false;
        state.approvalError = action.payload ?? 'Failed to approve OT records';
      })

      .addCase(fetchOTRateHistory.pending, (state) => {
        state.otRateHistoryLoading = true;
        state.otRateHistoryError = null;
      })
      .addCase(fetchOTRateHistory.fulfilled, (state, action) => {
        state.otRateHistoryLoading = false;
        state.otRateHistory = action.payload || [];
      })
      .addCase(fetchOTRateHistory.rejected, (state, action) => {
        state.otRateHistoryLoading = false;
        state.otRateHistoryError = action.payload ?? 'Failed to load OT history';
      });
  },
});

export const {
  clearOvertimeError,
  resetOvertime,
  setApprovalFilter,
  clearBulkApprovalResult,
} = overtimeSlice.actions;

export default overtimeSlice.reducer;